const EMPTY = { browser: "-", device: "-", os: "-", deviceType: "-" };

function matchGroup(ua, regex) {
  const m = ua.match(regex);
  return m?.[1] || "";
}

function majorVersion(version) {
  const v = String(version || "").split(".")[0];
  return v || "";
}

function detectBrowser(ua) {
  const edge = matchGroup(ua, /Edg(?:A|iOS)?\/([\d.]+)/);
  if (edge) return `Edge ${majorVersion(edge)}`;

  const opera = matchGroup(ua, /OPR\/([\d.]+)/);
  if (opera) return `Opera ${majorVersion(opera)}`;

  const samsung = matchGroup(ua, /SamsungBrowser\/([\d.]+)/);
  if (samsung) return `Samsung Internet ${majorVersion(samsung)}`;

  const firefox = matchGroup(ua, /Firefox\/([\d.]+)/);
  if (firefox) return `Firefox ${majorVersion(firefox)}`;

  const chrome = matchGroup(ua, /Chrome\/([\d.]+)/);
  if (chrome && !/Chromium/i.test(ua)) return `Chrome ${majorVersion(chrome)}`;

  const safari = matchGroup(ua, /Version\/([\d.]+).*Safari/);
  if (safari && /Safari\//.test(ua) && !/Chrome\//.test(ua)) {
    return `Safari ${majorVersion(safari)}`;
  }

  if (/MSIE |Trident\//.test(ua)) {
    const ie = matchGroup(ua, /MSIE ([\d.]+)/) || matchGroup(ua, /rv:([\d.]+)/);
    return ie ? `Internet Explorer ${majorVersion(ie)}` : "Internet Explorer";
  }

  if (/curl/i.test(ua)) return "cURL";
  if (/PostmanRuntime/i.test(ua)) return "Postman";
  if (/axios/i.test(ua)) return "Axios";
  if (/node-fetch|undici|Node\.js/i.test(ua)) return "Node.js";

  return "Unknown";
}

function detectOs(ua) {
  if (/Windows NT 10\.0/.test(ua)) return "Windows 10/11";
  if (/Windows NT 6\.3/.test(ua)) return "Windows 8.1";
  if (/Windows NT 6\.2/.test(ua)) return "Windows 8";
  if (/Windows NT 6\.1/.test(ua)) return "Windows 7";
  if (/Windows/.test(ua)) return "Windows";

  const mac = matchGroup(ua, /Mac OS X ([\d_]+)/);
  if (mac) return `macOS ${mac.replace(/_/g, ".")}`;

  const android = matchGroup(ua, /Android ([\d.]+)/);
  if (android) return `Android ${majorVersion(android)}`;

  const ios = matchGroup(ua, /(?:iPhone OS|CPU OS) ([\d_]+)/);
  if (ios) return `iOS ${ios.replace(/_/g, ".")}`;

  if (/iPad/.test(ua)) return "iPadOS";
  if (/CrOS/.test(ua)) return "Chrome OS";
  if (/Linux/.test(ua)) return "Linux";

  return "Unknown";
}

function detectDeviceType(ua) {
  if (/iPad|Tablet|PlayBook|Silk\/(?!.*Mobile)/i.test(ua)) return "Tablet";
  if (/Mobile|iPhone|Android.*Mobile|Windows Phone/i.test(ua)) return "Mobile";
  if (/Smart-TV|SmartTV|GoogleTV|AppleTV|HbbTV/i.test(ua)) return "TV";
  if (/Bot|crawler|spider|Slurp|bingbot|Googlebot/i.test(ua)) return "Bot";
  return "Desktop";
}

const cache = new Map();
const CACHE_MAX = 500;

/**
 * Parse a User-Agent string into browser, OS, and device type.
 * @param {string|null|undefined} ua
 * @returns {{ browser: string, device: string, os: string, deviceType: string }}
 */
export function parseUserAgent(ua) {
  const raw = ua == null || ua === "" ? "" : String(ua).trim();
  if (!raw) return { ...EMPTY };

  if (cache.has(raw)) return cache.get(raw);

  const os = detectOs(raw);
  const deviceType = detectDeviceType(raw);
  const browser = detectBrowser(raw);
  const device = deviceType === "Unknown" && os === "Unknown" ? "-" : `${os} · ${deviceType}`;

  const result = { browser, device, os, deviceType };
  if (cache.size >= CACHE_MAX) cache.clear();
  cache.set(raw, result);
  return result;
}

export function getBrowserFromUserAgent(ua) {
  return parseUserAgent(ua).browser;
}

export function getDeviceFromUserAgent(ua) {
  return parseUserAgent(ua).device;
}
